import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import { SidebarLayout, SidebarFilterToggle } from 'components/layout/SidebarLayout';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import CompanySidebarFilter from 'components/widgets/CompanySearch/CompanySidebarFilter';
import NoResultsFound from 'components/widgets/CompanySearch/NoResultsFound';
import RequestCompanyCTA from 'components/widgets/CompanySearch/RequestCompanyCTA';
import CompanySearchTable from 'components/widgets/CompanySearch/Table';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCompanySearch } from 'hooks/useCompanySearch';
import { useDebounce } from 'hooks/useDebounced';
import { useFilterUrlSync } from 'hooks/useFilterUrlSync';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedCompanies } from 'hooks/useSortedCompanies';
import {
  useCompanyCityFilter,
  useCompanyRegionFilter,
  useCompanyStateFilter,
  useCompanyTypeFilterState,
} from 'state/filterCompanies';
import { trackSearch } from 'utils/analytics';
import { CompanyInsightsPageViewSections } from 'utils/analytics/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { formatInteger } from 'utils/format';

// Company search selection persisted to the URL under `?f=<compressed>`. Keys
// are kept short to keep the compressed blob small.
type CompanySearchFilterUrlState = {
  q?: string; // company-name search query
  s?: string; // state code
  r?: string[]; // region (mercator metropolitan) codes
  c?: string[]; // city codes
  ct?: string[]; // company type values
};

const containerStyle = {
  paddingBottom: 'padding_24',
  paddingTop: 'padding_24',
  paddingLeft: 'padding_32',
  paddingRight: 'padding_32',
  marginX: 'auto',
  overflowY: 'auto',
  height: '100%',
  width: '100%',
};

const tableCardStyle = {
  marginTop: 'padding_24',
};

const noResultsFoundStyle = {
  paddingTop: 'padding_8',
};

const searchInputStyle = {
  boxShadow: '0px 8px 16px 0px #0000001F',
  height: '48px',
};

const searchRowStyle = {
  gap: 'padding_16',
  alignItems: 'stretch',
};

const searchInputWrapperStyle = {
  flex: 1,
};

export const CompanySearch = () => {
  const hasMountedRef = useRef(false);
  const {
    nextPage,
    previousPage,
    page: currentPage,
    setPageNumber,
    calcTotalPages,
  } = usePaginate();
  const { usePageViewTracking } = useAnalytics();
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounce(searchString, 500);
  const { stateFilter, setStateFilter } = useCompanyStateFilter();
  const { regionFilter, setRegionFilter } = useCompanyRegionFilter();
  const { cityFilter, setCityFilter } = useCompanyCityFilter();
  const { companyTypeFilter, setCompanyTypeFilter } = useCompanyTypeFilterState();

  // --- Search + filters <-> URL sync (?f=<compressed>) ---
  // Encode the search input and active filters into the URL and, on mount,
  // restore them from a shared/refreshed link. Empty values are omitted so the
  // param is dropped when nothing is active.
  const filterUrlState = useMemo<CompanySearchFilterUrlState>(() => {
    const next: CompanySearchFilterUrlState = {};
    if (searchString) next.q = searchString;
    if (stateFilter) next.s = stateFilter;
    if (regionFilter.length > 0) next.r = regionFilter;
    if (cityFilter.length > 0) next.c = cityFilter;
    if (companyTypeFilter.length > 0) next.ct = companyTypeFilter;

    return next;
  }, [searchString, stateFilter, regionFilter, cityFilter, companyTypeFilter]);

  const applyFilterUrlState = useCallback(
    (state: CompanySearchFilterUrlState) => {
      if (state.q !== undefined) setSearchString(state.q);
      if (state.s !== undefined) setStateFilter(state.s);
      if (state.r !== undefined) setRegionFilter(state.r);
      if (state.c !== undefined) setCityFilter(state.c);
      if (state.ct !== undefined) setCompanyTypeFilter(state.ct);
    },
    [setStateFilter, setRegionFilter, setCityFilter, setCompanyTypeFilter],
  );

  useFilterUrlSync({
    paramKey: QUERY_PARAM.filters,
    state: filterUrlState,
    apply: applyFilterUrlState,
    debounceMs: 300,
  });

  const debouncedCitySelections = useDebounce(cityFilter, 400);
  const debouncedCompanyTypeSelections = useDebounce(companyTypeFilter, 400);
  const { sortArray } = useSortedCompanies();
  const { filteredCompanies } = useCompanySearch({
    companyNameQuery: debouncedSearchString,
    projectCityCodes: debouncedCitySelections,
    companyTypes: debouncedCompanyTypeSelections,
    page: currentPage,
    sort: sortArray,
  });

  usePageViewTracking({
    sectionName: CompanyInsightsPageViewSections.COMPANY_DIRECTORY,
    subSectionName: CompanyInsightsPageViewSections.COMPANY_DIRECTORY,
  });

  useEffect(() => {
    // Skip the initial render (including search/filter hydration from the URL)
    // so we only track searches the user actually performs.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;

      return;
    }
    if (debouncedSearchString) {
      trackSearch({ search_input: debouncedSearchString });
    }
  }, [debouncedSearchString]);

  const resetPageNumber = () => {
    if (currentPage > 1) {
      setPageNumber(1);
    }
  };

  const handleSearchQuery = (newSearchQuery: string) => {
    resetPageNumber();
    setSearchString(newSearchQuery);
  };

  const totalCount = useMemo(() => {
    return filteredCompanies?.data?.count || 0;
  }, [filteredCompanies?.data]);

  const totalCountLabel = useMemo(() => {
    if (totalCount === 1) {
      return `${formatInteger(totalCount)} company found`;
    }

    return `${formatInteger(totalCount)} companies found`;
  }, [totalCount]);

  const totalPages = useMemo(() => {
    return calcTotalPages(totalCount) || 1;
  }, [totalCount]);

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Company Search" />
      <SidebarLayout
        hideSidebarOnMobile
        sidebarZIndex={Z_INDEX_DICT.COMPANY_FILTERS_MENU}
        sidebar={<CompanySidebarFilter />}
      >
        <FlexColumn sx={containerStyle}>
          <FlexRow sx={searchRowStyle}>
            <SidebarFilterToggle />
            <Box sx={searchInputWrapperStyle}>
              <SearchInput
                value={searchString}
                onChange={handleSearchQuery}
                placeholder="Search any company name or website"
                sx={searchInputStyle}
              />
            </Box>
          </FlexRow>

          <CompanySearchTable
            companyList={filteredCompanies?.data?.results || []}
            isLoading={filteredCompanies.isLoading}
            cardSx={tableCardStyle}
            header={totalCountLabel}
            footer={
              <Pagination
                currentPage={currentPage}
                isLoading={filteredCompanies.isLoading}
                totalPages={totalPages}
                setPage={setPageNumber}
                onNext={nextPage}
                onPrev={previousPage}
              />
            }
          />
          {totalCount === 0 && !filteredCompanies.isLoading && (
            <Box sx={noResultsFoundStyle}>
              <NoResultsFound companyNameQuery={debouncedSearchString} />
            </Box>
          )}
          {totalCount > 0 && !filteredCompanies.isLoading && debouncedSearchString && (
            <RequestCompanyCTA
              companyNameQuery={debouncedSearchString}
              showMessage={false}
            />
          )}
        </FlexColumn>
      </SidebarLayout>
    </BasePageWithTopNav>
  );
};
